// ============================================================================
// FSA SINGLE-NOTE PREVIEW MODAL
// ============================================================================
// Fetches the server-rendered single-note PDF (rendered strictly from
// saved data — no TB recompute), renders it inline in an <iframe>, and
// offers Download + Open/Print. Toggle at the top of the modal flips
// between Actual and Rounded amount modes — each toggle triggers a fresh
// fetch so the iframe always shows what the user picked.
//
// Mirrors FsaTemplatePdfPreviewModal — same iframe pattern, same
// blob-URL lifecycle. The print spec for single-note today is a 1-item
// envelope; statements / reports / multi-note bundles arrive in FSA-3.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { fsaPrintService } from 'api/services/fsa/fsaPrintService';
import notificationService from 'services/notificationService';
import type { ID } from 'api/types';
import type { FsAmountMode } from 'api/types/fsa/print.types';

interface FsaNotePreviewModalProps {
  isOpen: boolean;
  engagementId: ID | null;
  scheduleId: ID | null;
  /** Optional header chip — falls back to scheduleId when unset. */
  scheduleCode?: string | null;
  scheduleName?: string | null;
  /**
   * TRUE when the Note has edits that haven't been saved. Preview is NEVER
   * blocked on this — the auditor is the final authority and may preview at
   * any time — but the PDF is rendered strictly from SAVED data, so what's
   * on screen and what's in this document can differ. The banner says so
   * explicitly rather than letting the difference pass unnoticed.
   */
  hasUnsavedChanges?: boolean;
  onClose: () => void;
}

const FsaNotePreviewModal: React.FC<FsaNotePreviewModalProps> = ({
  isOpen,
  engagementId,
  scheduleId,
  scheduleCode,
  scheduleName,
  hasUnsavedChanges = false,
  onClose,
}) => {
  const [mode, setMode] = useState<FsAmountMode>('rounded');
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-fetch whenever the modal opens for a new (engagement, schedule)
  // or the mode toggles. Old blob URL is revoked on every change so we
  // don't leak.
  useEffect(() => {
    if (!isOpen || !engagementId || !scheduleId) return;

    let revoke: string | null = null;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      setBlobUrl(null);
      try {
        const blob = await fsaPrintService.getSingleNotePdfBlob(
          engagementId,
          scheduleId,
          mode,
        );
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoke = url;
        setBlobUrl(url);
      } catch (err: any) {
        if (cancelled) return;
        // Blob channel doesn't carry the JSON envelope. The server sends
        // 4xx as JSON when validation fails (e.g. "note not saved yet") —
        // we read the blob back as text so the message surfaces.
        let message =
          err?.response?.data?.message || err?.message || 'Failed to load preview.';
        if (err?.response?.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
          } catch {
            /* keep generic message */
          }
        }
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [isOpen, engagementId, scheduleId, mode]);

  const downloadName = scheduleCode
    ? `FSA-Note-${scheduleCode}-${mode}.pdf`
    : scheduleId
    ? `FSA-Note-${scheduleId}-${mode}.pdf`
    : 'FSA-Note.pdf';

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    notificationService.toast.success('Download started.');
  }, [blobUrl, downloadName]);

  const handlePrint = useCallback(() => {
    if (!blobUrl) return;
    const opened = window.open(blobUrl, '_blank');
    if (!opened) {
      notificationService.toast.warning(
        'Pop-up blocked. Allow pop-ups from this site so the PDF can open in a new tab.',
        6000,
      );
    }
  }, [blobUrl]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        className="modal-container modal-xl"
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
      >
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              minWidth: 0,
              flex: 1,
            }}
          >
            <IconSystem.print size={16} />
            <h2
              className="modal-title"
              style={{
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Note Preview
              {(scheduleCode || scheduleName) && (
                <span
                  style={{
                    fontSize: '0.85rem',
                    color: 'var(--text-secondary)',
                    marginLeft: '0.5rem',
                    fontWeight: 500,
                  }}
                >
                  · {scheduleCode}
                  {scheduleName ? ` — ${scheduleName}` : ''}
                </span>
              )}
            </h2>
          </div>
          <div style={modeToggleWrapStyle}>
            <button
              type="button"
              onClick={() => setMode('rounded')}
              style={{ ...modeToggleStyle, ...(mode === 'rounded' ? modeToggleActiveStyle : {}) }}
              disabled={loading}
              title="Show auditor-approved rounded values"
            >
              Rounded
            </button>
            <button
              type="button"
              onClick={() => setMode('actual')}
              style={{ ...modeToggleStyle, ...(mode === 'actual' ? modeToggleActiveStyle : {}) }}
              disabled={loading}
              title="Show full-precision actual values"
            >
              Actual
            </button>
          </div>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div
          className="modal-content"
          style={{
            flex: 1,
            padding: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-secondary)',
          }}
        >
          {hasUnsavedChanges && (
            <div style={unsavedBannerStyle}>
              <IconSystem.warning size={14} />
              <span>
                <strong>Rendered from the last saved version.</strong> This Note has unsaved
                changes — they are not reflected below. Save the Note and preview again to see them.
              </span>
            </div>
          )}
          {loading && (
            <div style={loadingStyle}>
              <div style={spinnerStyle} />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                Rendering PDF…
              </span>
            </div>
          )}

          {error && !loading && (
            <div style={errorStyle}>
              <IconSystem.alert size={28} />
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                Could not load preview
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {error}
              </div>
            </div>
          )}

          {blobUrl && !loading && !error && (
            <iframe
              src={`${blobUrl}#toolbar=1&navpanes=0`}
              title="FSA Single-Note Preview PDF"
              style={{
                flex: 1,
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '60vh',
              }}
            />
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-secondary"
            onClick={handlePrint}
            disabled={loading || !blobUrl || !!error}
            title="Open in a new tab — use the browser's Print option from there"
          >
            <IconSystem.print size={16} />
            Open / Print
          </button>
          <button
            className="btn btn-primary"
            onClick={handleDownload}
            disabled={loading || !blobUrl || !!error}
          >
            <IconSystem.download size={16} />
            Download PDF
          </button>
        </div>
      </div>
    </>
  );
};

// ───── styles ───────────────────────────────────────────────────────────────

const modeToggleWrapStyle: React.CSSProperties = {
  display: 'inline-flex',
  borderRadius: 6,
  border: '1px solid var(--form-border-color)',
  overflow: 'hidden',
  marginRight: 8,
};
const modeToggleStyle: React.CSSProperties = {
  padding: '4px 10px',
  fontSize: '0.75rem',
  fontWeight: 600,
  background: 'var(--bg-primary)',
  color: 'var(--text-secondary)',
  border: 'none',
  cursor: 'pointer',
};
const modeToggleActiveStyle: React.CSSProperties = {
  background: '#2563eb',
  color: '#ffffff',
};

// Amber advisory strip above the rendered PDF. Deliberately advisory, not
// obstructive: the auditor may preview whenever they like, but must never be
// left thinking the document reflects on-screen edits that it doesn't.
const unsavedBannerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '8px 12px',
  background: '#fef3c7',
  color: '#92400e',
  borderBottom: '1px solid #f59e0b',
  fontSize: '0.8rem',
  lineHeight: 1.4,
};

const loadingStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.75rem',
};
const spinnerStyle: React.CSSProperties = {
  width: '2rem',
  height: '2rem',
  border: '3px solid var(--border-color)',
  borderTopColor: '#2563eb',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};
const errorStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  padding: '2rem',
  textAlign: 'center',
};

export default FsaNotePreviewModal;
